import type {
  Channel as CommunityConfigChannel,
  CommentsConfig,
} from '~publish/pages/Community/CommunityConfig'
import { canPerform } from '~publish/utils/permissions/canPerform'

/**
 * Narrowest channel shape this predicate reads: `id` + `isDisconnected` plus the
 * `service`/`type`/`allowedActions` the Community config and `canPerform`
 * consume (carried by {@link CommunityConfigChannel}).
 */
type AutoBackfillChannel = CommunityConfigChannel & {
  id: string
  isDisconnected?: boolean | null
}

/**
 * Whether a channel should be auto-backfilled on Community page load.
 *
 * A channel qualifies when it is:
 * 1. Supported by the Community config,
 * 2. A polling channel (not realtime sync),
 * 3. Connected (not disconnected),
 * 4. One the actor can **manage comments** on, and
 * 5. Not already backfilling.
 *
 * (4) gates on the comments-scoped `manageComments` action rather than the
 * channel's `accessLevel`. The Community sidebar loads channels filtered by
 * `product: publish`, so `accessLevel` reflects publish access — under
 * per-product permissions a member can be publish `fullPosting` but comments
 * `none`/`requiresApproval`. The backfill mutation's authz requires
 * `manageComments` (comments `fullPosting`), so gating on `accessLevel` fired
 * the mutation for members who then hit an authz rejection (surfaced as a
 * generic error). `allowedActions` is the BE-derived source of truth and
 * mirrors that authz exactly.
 *
 * (3) only checks `isDisconnected`, not `isLocked`. The `manageComments` rule
 * already withholds the action on locked channels (its BE config sets
 * `mustBeUnlocked: true`), so a separate `isLocked` guard would be redundant.
 * It does NOT, however, factor in connection state: `isDisconnected` maps to
 * `credentials.invalid`, which the rule never inspects — so a disconnected but
 * unlocked channel can still report `manageComments`. The explicit
 * `isDisconnected` check keeps us from auto-backfilling those.
 *
 * `isAlreadyBackfilling` is injected so the cookie dependency stays out of this
 * pure predicate (and is trivially testable).
 */
export const isChannelEligibleForAutoBackfill = (
  channel: AutoBackfillChannel,
  config: Pick<CommentsConfig, 'isChannelSupported' | 'isRealtimeSync'>,
  isAlreadyBackfilling: (channelId: string) => boolean,
): boolean => {
  const isSupported = config.isChannelSupported(channel)
  const isPolling = !config.isRealtimeSync(channel)
  const isConnected = !channel.isDisconnected
  const canManageComments = canPerform(channel, 'manageComments')
  const isNotAlreadyBackfilling = !isAlreadyBackfilling(channel.id)

  return (
    isSupported &&
    isPolling &&
    isConnected &&
    canManageComments &&
    isNotAlreadyBackfilling
  )
}
